import React, { useState, useEffect } from 'react';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import HourProgressCard from '../components/hours/HourProgressCard';
import HourCategoryProgress from '../components/hours/HourCategoryProgress';
import HourEntryForm from '../components/hours/HourEntryForm';
import HourEntryList from '../components/hours/HourEntryList';

const STATUS_LABELS = {
    draft: 'Draft',
    submitted: 'Submitted',
    verified: 'Verified',
    approved: 'Approved',
    returned: 'Needs Update',
    rejected: 'Needs Update'
};

const Hours = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        loadHours();
    }, [refreshKey]);

    const loadHours = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/hours/intern');
            setData(response.data.data);
        } catch (error) {
            toast.error('Unable to load your practical hours right now.');
        } finally {
            setLoading(false);
        }
    };

    const handleFormSuccess = () => {
        setShowForm(false);
        setEditingEntry(null);
        setRefreshKey((prev) => prev + 1);
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-64" />
                <div className="skeleton h-32" />
                <div className="skeleton h-48" />
                <span className="sr-only">Loading your practical hours…</span>
            </div>
        );
    }

    const summary = data?.summary;
    const friendlyCounts = (data?.statusCounts || []).map((row) => ({
        ...row,
        label: STATUS_LABELS[row.status] || row.status
    }));

    return (
        <div className="w-full space-y-6 overflow-x-hidden">
            <PageHeader
                title="Practical Hours"
                description={
                    summary?.template_name
                        ? `${summary.template_name} program requirement. Only approved hours count toward completion.`
                        : 'Track approved internship hours toward completion. Only approved hours count.'
                }
                actions={
                    <button
                        type="button"
                        onClick={() => {
                            setEditingEntry(null);
                            setShowForm(!showForm);
                        }}
                        className="btn-primary"
                    >
                        {showForm && !editingEntry ? (
                            'Cancel'
                        ) : (
                            <>
                                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                                Log hours
                            </>
                        )}
                    </button>
                }
            />

            <HourProgressCard summary={summary} />

            <div className="card animate-fade-up">
                <h2 className="text-lg font-semibold text-ink-950 mb-1">Category progress</h2>
                <p className="text-sm text-ink-600 mb-4">Approved hours / allocated hours for your program template.</p>
                <HourCategoryProgress categories={data?.categories} />
            </div>

            {(showForm || editingEntry) && (
                <div className="card animate-fade-up">
                    <h2 className="text-lg font-semibold text-ink-950 mb-4">
                        {editingEntry ? 'Update hour entry' : 'Log practical hours'}
                    </h2>
                    <HourEntryForm
                        entry={editingEntry}
                        onSuccess={handleFormSuccess}
                        onCancel={() => {
                            setShowForm(false);
                            setEditingEntry(null);
                        }}
                    />
                </div>
            )}

            <div className="card animate-fade-up">
                <h2 className="text-lg font-semibold text-ink-950 mb-4">Hour entries</h2>
                <HourEntryList
                    entries={data?.recentEntries || []}
                    onUpdate={loadHours}
                    onEdit={(entry) => {
                        setEditingEntry(entry);
                        setShowForm(true);
                    }}
                />
            </div>

            {friendlyCounts.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {friendlyCounts.map((status, i) => (
                        <div key={status.status} className={`kpi-card text-center animate-fade-up stagger-${Math.min(4, i + 1)}`}>
                            <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">{status.label}</p>
                            <p className="mt-2 text-xl font-bold text-ink-950 tabular-nums">{status.count}</p>
                            <p className="text-xs text-ink-500 mt-1">{status.total_hours} hrs</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Hours;
